import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ChevronDown, Eye, EyeOff, Hourglass, FolderInput, Forward, Inbox, ListChecks, MoreHorizontal,
  NotebookPen, RefreshCw, Reply, ShieldAlert, SquareCheckBig, Trash2, X as XIcon,
} from "lucide-react"
import { api } from "@/lib/api"
import { toast } from "@/components/portal/Toast"
import { CallTaskModal } from "@/components/portal/CallTaskModal"
import { InterneAufgabeModal } from "@/components/portal/InterneAufgabeModal"
import { useNotizen, useWartetSetzen } from "./zusammenarbeit"
import { useGelesen } from "./gelesen"

/**
 * Die Aktionsleiste ueber der geoeffneten Mail — Status, Antworten,
 * Weiterleiten, Verschieben, Neu analysieren, Aufgabe, Loeschen.
 *
 * Aus pages/EmailsList.tsx herausgeloest (Schritt G).
 */

export function parseContactPrefill(e: any): {
  prefillFirst?: string
  prefillLast?: string
  prefillEmail?: string
  prefillPhone?: string
  prefillStreet?: string
  prefillZipcode?: string
  prefillCity?: string
  prefillDescription?: string
} {
  let cd: any = {}
  try {
    if (e.contact_data_json) cd = JSON.parse(e.contact_data_json) || {}
  } catch { /* ignore */ }

  // Fallback fuer Name: aus Sender-Header "Vorname Nachname <mail@..>"
  let first = cd.first_name || ""
  let last = cd.last_name || ""
  if (!first && !last) {
    const sender = (e.sender || "").trim()
    const displayMatch = sender.match(/^"?([^"<]+?)"?\s*</)
    const display = (displayMatch ? displayMatch[1] : (sender.includes("@") ? "" : sender)).trim()
    if (display) {
      const parts = display.split(/\s+/)
      first = parts[0] || ""
      last = parts.slice(1).join(" ") || ""
    }
  }

  // Fallback fuer die Adresse: aus dem Sender-Header herausziehen.
  // `sender_email` ist bei 593 von 4877 eingehenden Mails leer, obwohl die
  // Adresse in `sender` steht ("Marcel Musterfried <m.musterfried@firma-musterfried.example>")
  // — bei 505 davon waere sie so zu retten. Ohne das blieb das E-Mail-Feld im
  // Anlege-Dialog leer, obwohl die Adresse im Kopf sogar markiert war (#10).
  const adresseAusSender = ((e.sender || "").match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/) || [""])[0]

  return {
    prefillFirst: first,
    prefillLast: last,
    prefillEmail: cd.email || e.sender_email || adresseAusSender || "",
    prefillPhone: cd.phone || cd.phone_mobile || "",
    prefillStreet: cd.street || "",
    // Backend-Analyse liefert den Key "zip" (siehe claude_email.py Prompt) —
    // der fehlte hier, PLZ kam nie im Modal an (Bug 06.07.2026)
    prefillZipcode: cd.zipcode || cd.postcode || cd.zip || "",
    prefillCity: cd.city || "",
    prefillDescription: e.subject || "",
  }
}

/** Extrahiert die reine E-Mail-Adresse aus einem Reply-To-Header ("Name <foo@bar>"). */
export function extractReplyAddress(header?: string | null): string {
  if (!header) return ""
  const m = header.match(/<([^>]+)>/)
  if (m) return m[1].trim()
  if (header.includes("@")) return header.trim()
  return ""
}

/** Spam-/Junk-Ordner? Dieselbe Regel wie das Backend
 *  (`database/emails.py` SPAM_ORDNER_SQL): letzter Namensteil beginnt mit spam/junk. */
export function istSpamOrdner(ordner?: string | null): boolean {
  return /(^|[/.])(spam|junk)[^/.]*$/.test((ordner || "").toLowerCase())
}

export type ForwardRule = { category_pattern?: string; forward_to: string; label: string }

export function ActionBar({ email: e, emailId, onClose, onReply, onQuickForward, aufgabenText, karte, aufgabePlan }: {
  email: any; emailId: number; onClose: () => void
  /** Klappt den Antwort-Entwurf in der Detailansicht auf. */
  onReply?: () => void
  onQuickForward?: (to: string) => void
  /** Vorbelegung fuer "Aufgabe aus E-Mail" (#188) — fertig berechnet:
   *  Plan-Aufgabentext, sonst `handlungsbedarf`, sonst Mail-Auszug.
   *  Nie die KI-Begruendung (`reasoning`). */
  aufgabenText?: string
  /** Neues Postfach: „Wartet auf Kunde“ (selten) liegt unter ···, die Leiste bleibt kurz. */
  karte?: boolean
  /** #128: Frist, Empfaenger und Zwischenantwort aus dem Plan (`aufgabeAusPlan`). */
  aufgabePlan?: { frist: string | null; empfaenger: string | null; zwischenantwort: boolean }
}) {
  const qc = useQueryClient()
  const [moveOpen, setMoveOpen] = useState(false)
  // Verschieben passiert „häufig am Tag" (Paul 14.10.2026): zuletzt benutzte
  // Ordner oben, Suchfeld bei vielen Ordnern. Gemerkt je Betrieb im Browser.
  const [moveSuche, setMoveSuche] = useState("")
  const zuletztSchluessel = `ev2_zuletzt_ordner_${e.tenant_slug || ""}`
  const zuletztLesen = (): string[] => {
    try { return JSON.parse(localStorage.getItem(zuletztSchluessel) || "[]") as string[] } catch { return [] }
  }
  const zuletztMerken = (f: string) => {
    try { localStorage.setItem(zuletztSchluessel, JSON.stringify([f, ...zuletztLesen().filter((x) => x !== f)].slice(0, 4))) } catch { /* privat */ }
  }
  const [err, setErr] = useState<string | null>(null)

  // Forward-Rules (pro Tenant) — zeigen einen Quick-Button wenn Betreff/Kategorie matcht
  const rulesQ = useQuery({
    queryKey: ["forward-rules"],
    queryFn: async () => {
      const r = await fetch("/api/v1/forward-rules", { credentials: "include" })
      if (!r.ok) return {} as Record<string, ForwardRule[]>
      return (await r.json()) as Record<string, ForwardRule[]>
    },
    staleTime: 1000 * 60 * 5,
  })
  /** Alle hinterlegten Weiterleitungs-Ziele dieses Betriebs. */
  const alleRegeln: ForwardRule[] = (rulesQ.data?.[e.tenant_slug] || [])
    .filter((r) => (r.forward_to || "").trim())
  const matchedRule: ForwardRule | null = (() => {
    const list = rulesQ.data?.[e.tenant_slug] || []
    if (!list.length) return null
    const hay = `${(e.category || "").toLowerCase()} ${(e.subject || "").toLowerCase()}`.trim()
    if (!hay) return null
    for (const r of list) {
      if (!r.category_pattern) continue
      try {
        if (new RegExp(r.category_pattern, "i").test(hay)) return r
      } catch { /* invalid regex */ }
    }
    return null
  })()

  // Echte IMAP-Ordner des Tenants laden — nicht nur die 4 Default-Labels
  const imapFolders = useQuery({
    // Verschieben geht nur innerhalb des Postfachs, in dem die Mail liegt —
    // also dessen Ordner anbieten (bei einem Postfach: wie bisher).
    queryKey: ["imap-folders", e.tenant_slug || "", e.postfach || "haupt"],
    queryFn: async () => {
      if (!e.tenant_slug) return [] as string[]
      const r = await fetch(`/api/v1/folders?tenant=${encodeURIComponent(e.tenant_slug)}&postfach=${encodeURIComponent(e.postfach || "haupt")}`, { credentials: "include" })
      if (!r.ok) return [] as string[]
      const data = await r.json()
      return Array.isArray(data) ? (data as string[]) : []
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!e.tenant_slug,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
    qc.invalidateQueries({ queryKey: ["emails"] })
  }

  const move = useMutation({
    mutationFn: async (folder: string) => {
      const res = await fetch(`/api/v1/emails/${emailId}/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ folder }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`)
      return j
    },
    // 🔴 Erst schliessen und melden, DANN neu laden (25.08.2026).
    //
    // `invalidate()` zieht die ganze Liste neu; bis sie da ist, steht der
    // Dialog offen und die Mail sichtbar an ihrem alten Platz. Der Server
    // antwortet inzwischen sofort — das Warten kam von hier.
    onSuccess: (_, folder) => {
      setMoveOpen(false)
      setMoveSuche("")
      zuletztMerken(folder)
      toast(`✓ E-Mail verschoben → ${folder}`)
      onClose()
      invalidate()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Verschieben fehlgeschlagen: " + msg, "error")
    },
  })

  const del = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/emails/${emailId}`, {
        method: "DELETE",
        credentials: "include",
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`)
      return j as { ok: boolean; imap_moved_to?: string | null }
    },
    // Seit 25.08.2026 wandert die Mail im Hintergrund in den Papierkorb —
    // „nur in Portal gelöscht" waere jetzt falsch: es ist noch nicht
    // gespiegelt, aber es wird, mit Wiederholungen. Scheitert es endgueltig,
    // steht es als Fehler im Aktivitätsprotokoll.
    onSuccess: () => {
      toast("✓ Gelöscht")
      onClose()
      invalidate()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Löschen fehlgeschlagen: " + msg, "error")
    },
  })

  // Knöpfe „Spam" / „Kein Spam" (Paul 16.10.2026): verschiebt in den
  // Spam-Ordner des Postfachs bzw. zurück und merkt sich den Absender.
  const imSpam = istSpamOrdner(e.imap_folder)
  const spamKnopf = useMutation({
    mutationFn: async (richtung: "spam" | "kein-spam") => {
      const res = await fetch(`/api/v1/emails/${emailId}/${richtung}`, { method: "POST", credentials: "include" })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(j.detail || j.error || `HTTP ${res.status}`)
      return { ...j, richtung } as { ordner: string; absender_gemerkt: boolean; richtung: string }
    },
    onSuccess: (j) => {
      toast(j.richtung === "spam"
        ? `✓ Als Spam nach „${j.ordner}“ verschoben${j.absender_gemerkt ? " — Absender wird künftig automatisch einsortiert" : ""}`
        : `✓ Zurück im Posteingang${j.absender_gemerkt ? " — Absender wird nie mehr als Spam einsortiert" : ""}`)
      onClose()
      invalidate()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Fehlgeschlagen: " + msg, "error")
    },
  })

  const reanalyze = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/emails/${emailId}/reanalyze`, { method: "POST", credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const j = await res.json().catch(() => null) as { category?: string | null } | null
      return j
    },
    onSuccess: (j) => {
      invalidate()
      if (j?.category === "Analyse fehlgeschlagen") {
        toast("KI-Analyse lieferte ungueltiges JSON — Mail bleibt im Review mit Marker.", "error")
      } else {
        toast("KI-Analyse aktualisiert.", "success")
      }
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Reanalyze fehlgeschlagen: " + msg, "error")
    },
  })

  const [taskOpen, setTaskOpen] = useState(false)
  const [internOpen, setInternOpen] = useState(false)

  // Frueher suchte der Knopf per Query-Selektor nach einer Textarea und klickte
  // notfalls einen Knopf mit passendem Text. Jetzt sagt es die Detailansicht
  // selbst — sie haelt den Zustand des Entwurfs.
  const scrollToReply = () => onReply?.()

  // Folder-Liste: IMAP-Ordner + Standard-Labels, dedupliziert, aktueller entfernt
  const defaultFolders = ["INBOX", "Archive", "Archiv", "Trash", "Papierkorb", "Sent"]
  const allFolders = Array.from(new Set([...(imapFolders.data || []), ...defaultFolders]))
  const currentFolder = e.imap_folder || "INBOX"
  const folders = allFolders.filter((f) => {
    if (f === currentFolder) return false
    // Gmail-System-Labels rausfiltern (außer Sent Mail / Drafts / Trash etc)
    if (f.startsWith("[Gmail]/") && !f.includes("Trash") && !f.includes("Sent")) return false
    return true
  })

  // „Wartet auf Kunde" von Hand (#191). Der Stand kommt vom Server, denn das
  // Ende (der Kunde hat geantwortet) wird dort berechnet.
  const eingehend = e.email_direction !== "sent"
  const notizen = useNotizen(eingehend ? emailId : 0)
  const wartetManuell = !!notizen.data?.wartet_manuell
  const wartet = useWartetSetzen(emailId)
  const wartetUmschalten = () => wartet.mutate(!wartetManuell, {
    onSuccess: () => {
      toast(wartetManuell ? "Wieder offen" : "Wartet auf Kunde — liegt jetzt unter „Wartet auf Kunde“", "success")
      if (!wartetManuell) onClose()
    },
    onError: (ex) => toast("Fehlgeschlagen: " + (ex instanceof Error ? ex.message : String(ex)), "error"),
  })

  const [moreOpen, setMoreOpen] = useState(false)
  // Neues Postfach: gelesen ist das Feld aus dem Postfach, nicht der Status —
  // „ungelesen“ darf die Mail nicht wieder in die Töpfe schieben (29.10.2026).
  const isRead = karte ? e.gelesen !== false : e.status !== "new"
  const isDone = e.status === "done"
  const gelesenSetzen = useGelesen()

  const setStatus = async (s: string) => {
    try {
      const { error } = await api.PATCH("/api/v1/emails/{email_id}", {
        params: { path: { email_id: emailId } },
        body: { status: s },
      })
      if (error) throw new Error(String(error))
      invalidate()
    } catch (ex) {
      toast("Fehler: " + (ex instanceof Error ? ex.message : String(ex)), "error")
    }
  }

  return (
    <div className="ev2-ab-reihe">
      {/* Sichtbar bleibt, was oft gebraucht wird. Der Rest liegt unter "···" —
          Loeschen, Markieren, Gelesen, Re-analysieren, Aufgabe. */}
      <button className="ev2-ab-btn" onClick={scrollToReply} title="Antworten">
        <Reply className="w-3.5 h-3.5" /> Antworten
      </button>
      <button
        className="ev2-ab-btn"
        title="Weiterleiten"
        onClick={() => {
          const ff = document.querySelector(`#forward-form-${emailId}`)
          ff?.scrollIntoView({ behavior: "smooth", block: "center" })
          const btn = ff?.querySelector("button") as HTMLButtonElement | null
          btn?.click()
        }}
      >
        <Forward className="w-3.5 h-3.5" /> Weiterleiten
      </button>
      {matchedRule && onQuickForward && (
        <button
          className="ev2-ab-btn"
          title={`An ${matchedRule.forward_to} weiterleiten`}
          onClick={() => onQuickForward(matchedRule.forward_to)}
        >
          <Forward className="w-3.5 h-3.5" /> {matchedRule.label}
        </button>
      )}

      {/* Verschieben-Dropdown */}
      <div style={{ position: "relative" }}>
        <button className="ev2-ab-btn" data-taste="verschieben" onClick={() => { setMoveOpen(!moveOpen); setMoveSuche("") }} title="Verschieben (V)">
          <FolderInput className="w-3.5 h-3.5" /> Verschieben <ChevronDown className="w-3 h-3" />
        </button>
        {moveOpen && (() => {
          const q = moveSuche.trim().toLowerCase()
          const zuletzt = zuletztLesen().filter((f) => folders.includes(f))
          const liste = q
            ? folders.filter((f) => f.toLowerCase().includes(q))
            : [...zuletzt, ...folders.filter((f) => !zuletzt.includes(f))]
          return (
          <div className="ev2-ab-menu">
            {folders.length > 6 && (
              <input autoFocus value={moveSuche} onChange={(ev) => setMoveSuche(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" && liste[0]) move.mutate(liste[0])
                  if (ev.key === "Escape") setMoveOpen(false)
                }}
                placeholder="Ordner suchen …" aria-label="Ordner suchen" className="ev2-ab-suche" />
            )}
            {!q && zuletzt.length > 0 && <div className="ev2-ab-menu-titel">Zuletzt benutzt</div>}
            {folders.length === 0 ? (
              <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--text-muted)" }}>
                Keine IMAP-Ordner geladen
              </div>
            ) : liste.length === 0 ? (
              <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--text-muted)" }}>
                Kein Ordner passt zu „{moveSuche}“
              </div>
            ) : liste.map((f, i) => (
              <button
                key={f}
                onClick={() => move.mutate(f)}
                disabled={move.isPending}
                className="ev2-ab-menu-item"
                style={!q && zuletzt.length > 0 && i === zuletzt.length ? { borderTop: "1px solid var(--border-light)" } : undefined}
              >
                {f}
              </button>
            ))}
          </div>
          )
        })()}
      </div>

      {eingehend && !karte && (
        <button
          className="ev2-ab-btn"
          data-testid="wartet-knopf"
          onClick={wartetUmschalten}
          disabled={wartet.isPending || notizen.isLoading}
          title={wartetManuell
            ? "Wieder offen: zurück nach „Offen“ (der Kunde hat noch nicht geantwortet)"
            : "Wartet auf Kunde: nimmt die Mail aus „Offen“, bis der Kunde antwortet"}
        >
          <Hourglass className="w-3.5 h-3.5" /> {wartetManuell ? "Wieder offen" : "Wartet auf Kunde"}
        </button>
      )}

      {e.email_direction !== "sent" && (imSpam ? (
        <button
          className="ev2-ab-btn ev2-ab-kein-spam"
          data-testid="kein-spam-knopf"
          onClick={() => spamKnopf.mutate("kein-spam")}
          disabled={spamKnopf.isPending}
          title="Kein Spam: zurück in den Posteingang, Absender wird nie mehr automatisch als Spam einsortiert"
        >
          <Inbox className="w-3.5 h-3.5" /> {spamKnopf.isPending ? "Verschiebe…" : "Kein Spam"}
        </button>
      ) : (
        <button
          className="ev2-ab-btn ev2-ab-spam"
          data-testid="spam-knopf"
          onClick={() => spamKnopf.mutate("spam")}
          disabled={spamKnopf.isPending}
          title="Spam: in den Spam-Ordner des Postfachs, künftige Mails dieses Absenders landen automatisch dort"
        >
          <ShieldAlert className="w-3.5 h-3.5" /> {spamKnopf.isPending ? "Verschiebe…" : "Spam"}
        </button>
      ))}

      {/* Alles Seltene */}
      <div style={{ position: "relative" }}>
        <button className="ev2-ab-btn ev2-ab-icon" onClick={() => setMoreOpen(!moreOpen)} title="Mehr">
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
        {moreOpen && (
          <div className="ev2-ab-menu" onMouseLeave={() => setMoreOpen(false)}>
            {karte && eingehend && (
              <button className="ev2-ab-menu-item" data-testid="wartet-knopf"
                onClick={() => { setMoreOpen(false); wartetUmschalten() }}
                disabled={wartet.isPending || notizen.isLoading}>
                <Hourglass className="w-3.5 h-3.5" /> {wartetManuell ? "Wieder offen" : "Wartet auf Kunde"}
              </button>
            )}
            <button
              className="ev2-ab-menu-item"
              data-testid="gelesen-knopf"
              onClick={() => {
                setMoreOpen(false)
                if (karte) gelesenSetzen([emailId], !isRead)
                else setStatus(isRead ? "new" : "reviewed")
              }}
            >
              {isRead ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {isRead ? "Als ungelesen markieren" : "Als gelesen markieren"}
            </button>
            <button
              className="ev2-ab-menu-item"
              onClick={() => { setMoreOpen(false); setStatus(isDone ? "reviewed" : "done") }}
            >
              <SquareCheckBig className="w-3.5 h-3.5" style={isDone ? { color: "#059669" } : undefined} />
              {isDone ? "Erledigt aufheben" : "Als erledigt markieren"}
            </button>
            <button
              className="ev2-ab-menu-item"
              onClick={() => { setMoreOpen(false); reanalyze.mutate() }}
              disabled={reanalyze.isPending}
            >
              <RefreshCw className="w-3.5 h-3.5" /> Re-analysieren
            </button>
            <button
              className="ev2-ab-menu-item"
              onClick={() => { setMoreOpen(false); setTaskOpen(true) }}
            >
              <ListChecks className="w-3.5 h-3.5" /> Aufgabe aus E-Mail
            </button>
            {/* Die interne Aufgabe daneben, bewusst getrennt benannt (#39):
                die eine geht nach HERO und vor den Monteur, die andere bleibt
                im Portal. Wer das verwechselt, schickt eine Buero-Notiz an den
                Kunden. Der globale Schnellzugriff ist auf dieser Seite
                ausgeblendet (`AppLayout.tsx`), und ohne Bezug zur Mail waere
                er hier ohnehin die halbe Sache. */}
            <button
              className="ev2-ab-menu-item"
              onClick={() => { setMoreOpen(false); setInternOpen(true) }}
            >
              <NotebookPen className="w-3.5 h-3.5" /> Interne Aufgabe (bleibt im Portal)
            </button>
            {/* #89 (Marlene, 10.09.): „wenn der Buchhaltungsbutton nicht angezeigt
                wird, sollte die Option trotzdem zb unter den Pünktchen aufgeführt
                sein". Der Knopf oben erscheint nur, wenn das Muster der Regel auf
                Kategorie und Betreff passt — bei einer Rechnung, die als Phishing
                einsortiert wurde, also nie. Hier stehen alle Ziele des Betriebs,
                unabhängig vom Muster.
                Es wird nichts gesendet: der Punkt öffnet das Weiterleiten-Formular
                mit vorbelegter Adresse, abgeschickt wird dort wie immer von Hand. */}
            {onQuickForward && alleRegeln
              .filter((r) => r.forward_to !== matchedRule?.forward_to)
              .map((r) => (
                <button
                  key={r.forward_to}
                  className="ev2-ab-menu-item"
                  title={`Weiterleiten an ${r.forward_to} — zum Prüfen, gesendet wird erst im Formular`}
                  onClick={() => { setMoreOpen(false); onQuickForward(r.forward_to) }}
                >
                  <Forward className="w-3.5 h-3.5" /> {r.label || `An ${r.forward_to}`}
                </button>
              ))}
            <button
              className="ev2-ab-menu-item ev2-ab-menu-danger"
              onClick={() => { setMoreOpen(false); if (confirm("E-Mail in Papierkorb verschieben?")) del.mutate() }}
              disabled={del.isPending}
            >
              <Trash2 className="w-3.5 h-3.5" /> {del.isPending ? "Löscht…" : "Löschen"}
            </button>
          </div>
        )}
      </div>

      <button className="ev2-ab-btn ev2-ab-icon" onClick={onClose} title="Schließen">
        <XIcon className="w-3.5 h-3.5" />
      </button>

      <CallTaskModal
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
        mode="email"
        callId={emailId}
        tenantSlug={e.tenant_slug || ""}
        defaultTitle={`Rückfrage: ${(e.subject || "").slice(0, 60)}`}
        // #188: Aufgabentext kommt aus `aufgabenVorbelegung` (Plan-Aufgabe,
        // `handlungsbedarf`, Mail-Auszug) — die KI-Begruendung (`reasoning`)
        // ist fuer den Pruefer, nicht fuer den Empfaenger der Aufgabe.
        defaultComment={(aufgabenText || e.handlungsbedarf || "").trim() || undefined}
        defaultDueDate={aufgabePlan?.frist}
        defaultAssignee={aufgabePlan?.empfaenger}
        onZwischenantwort={aufgabePlan?.zwischenantwort ? onReply : undefined}
      />

      {/* 🔑 Mit `emailId`: die Aufgabe traegt danach den Rueckweg zur Mail,
          und die Liste bietet ihn als „Mail"-Marke an (#39). */}
      <InterneAufgabeModal
        open={internOpen}
        onClose={() => setInternOpen(false)}
        tenantSlug={e.tenant_slug || null}
        emailId={emailId}
        titelVorschlag={(e.subject || "").slice(0, 120) || null}
      />

      {err && <div className="ev2-ab-fehler">{err}</div>}
    </div>
  )
}
